<template>
  <div class="admin-dashboard-root" ref="scrollContainer">
    <!-- 頂部標題 -->
    <div class="page-header">
      <h1 class="page-title">管理控制台</h1>
      <p class="page-subtitle">智能合約管理中心</p>
    </div>
    
    <!-- 管理卡片網格 -->
    <div class="management-grid">
      <!-- PCT 合約管理卡片 -->
      <div class="management-card" @click="navigateTo('/admin/pct')">
        <div class="card-icon">
          <img src="@/assets/images/pct_logo.png" alt="PCT" />
        </div>
        <div class="card-content">
          <h3 class="card-title">PCT 合約管理</h3>
          <p class="card-description">管理 PCT 合約相關設定，包括董事升級數量等</p>
        </div>
        <button class="card-action-btn">
          進入管理
        </button>
      </div>

      <!-- PCD 合約管理卡片 -->
      <div class="management-card" @click="navigateTo('/admin/pcd')">
        <div class="card-icon">
          <img src="@/assets/images/pcd_logo.png" alt="PCD" />
        </div>
        <div class="card-content">
          <h3 class="card-title">PCD 合約管理</h3>
          <p class="card-description">管理 PCD 合約相關設定，包括私募投資等</p>
        </div>
        <button class="card-action-btn">
          進入管理
        </button>
      </div>

      <!-- xPCT 合約管理卡片 -->
      <div class="management-card" @click="navigateTo('/admin/xpct')">
        <div class="card-icon">
          <img src="@/assets/images/pcd_logo.png" alt="xPCT" />
        </div>
        <div class="card-content">
          <h3 class="card-title">xPCT 合約管理</h3>
          <p class="card-description">管理 xPCT 合約相關設定，包括質押規則等</p>
        </div>
        <button class="card-action-btn">
          進入管理
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { onMounted, onUnmounted, ref } from 'vue';

const router = useRouter();
const scrollContainer = ref(null);

const navigateTo = (path) => {
  router.push(path);
};

// 確保滾動容器高度正確設置
const ensureScrollHeight = () => {
  if (scrollContainer.value) {
    // 強制重新計算高度
    scrollContainer.value.style.height = 'auto';
    const windowHeight = window.innerHeight;
    const contentHeight = scrollContainer.value.scrollHeight;
    
    if (contentHeight < windowHeight) {
      scrollContainer.value.style.minHeight = `${windowHeight}px`;
    }
  }
};

onMounted(() => {
  // 初始化高度
  ensureScrollHeight();
  
  // 監聽窗口大小變化
  window.addEventListener('resize', ensureScrollHeight);
  
  // 確保內容加載完成後重新計算
  setTimeout(ensureScrollHeight, 100);
});

onUnmounted(() => {
  window.removeEventListener('resize', ensureScrollHeight);
});
</script>

<style scoped>
/* CSS 變量定義 - 嚴格遵循設計規範 */
:root {
  --primary-gold: #10b981;
  --primary-gold-light: #6ee7b7;
  --primary-gold-dark: #059669;
  --primary-gold-muted: #047857;
  
  --bg-primary: #0a1a0a;
  --bg-secondary: #0d1a0d;
  --bg-tertiary: #152015;
  --bg-elevated: #1a2e1a;
  
  --accent-teal: #6ee7b7;
  --accent-teal-dark: #44a39a;
  --accent-warm: #f4e4bc;
  
  --text-primary: #6ee7b7;
  --text-secondary: #059669;
  --text-muted: #047857;
  --text-inverse: #0a1a0a;
  
  --transition-fast: 0.2s;
  --transition-base: 0.3s;
  --transition-slow: 0.5s;
  
  --ease-out: cubic-bezier(0, 0, 0.2, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
}

/* 全局滾動條樣式 - 黑金主題 */
.admin-dashboard-root::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

.admin-dashboard-root::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  margin: 10px 0;
}

.admin-dashboard-root::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
  border-radius: 6px;
  border: 2px solid var(--bg-primary);
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
}

.admin-dashboard-root::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, var(--primary-gold-light) 0%, var(--primary-gold) 100%);
  box-shadow: 
    inset 0 0 8px rgba(0, 0, 0, 0.4),
    0 0 12px rgba(16, 185, 129, 0.3);
}

/* Firefox 滾動條 */
.admin-dashboard-root {
  scrollbar-width: thin;
  scrollbar-color: var(--primary-gold) rgba(0, 0, 0, 0.4);
}

/* 根容器 - 優化滾動性能 */
.admin-dashboard-root {
  min-height: 100vh;
  background: var(--bg-primary);
  padding: 40px 20px 80px; /* 增加底部padding避免內容被遮擋 */
  position: relative;
  overflow-x: hidden;
  overflow-y: auto; /* 明確設置垂直滾動 */
  
  /* 滾動性能優化 */
  -webkit-overflow-scrolling: touch; /* iOS 滾動流暢度 */
  scroll-behavior: smooth; /* 平滑滾動 */
  
  /* 硬件加速 */
  transform: translateZ(0);
  will-change: scroll-position;
  
  /* 防止滾動時出現閃爍 */
  backface-visibility: hidden;
  perspective: 1000;
}

/* 頁面標題 - 遵循規範 */
.page-header {
  text-align: center;
  margin-bottom: 48px;
  animation: fadeIn 0.6s var(--ease-out);
}

.page-title {
  font-size: 36px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary-gold-light) 0%, var(--primary-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 8px 0;
  letter-spacing: 1px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-muted);
  margin: 0;
  opacity: 0.8;
}

/* 管理卡片網格 - 優化佈局 */
.management-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  padding-bottom: 40px; /* 確保底部內容可見 */
  
  /* 避免重排造成的性能問題 */
  contain: layout style;
}

/* 管理卡片 - 優化渲染性能 */
.management-card {
  background: linear-gradient(135deg, var(--bg-elevated) 0%, var(--bg-secondary) 100%);
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: 16px;
  padding: 28px;
  position: relative;
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-out);
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  /* 標準多層陰影 */
  box-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 4px 8px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.1);
    
  /* 內部光暈 */
  background-image: 
    linear-gradient(135deg, var(--bg-elevated) 0%, var(--bg-secondary) 100%),
    radial-gradient(ellipse at top left, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
    
  /* 性能優化 */
  transform: translateZ(0); /* 開啟硬件加速 */
  will-change: transform; /* 提示瀏覽器優化 */
}

/* 卡片懸浮效果 - 優化動畫 */
.management-card:hover {
  transform: translateY(-4px) translateZ(0);
  border-color: rgba(16, 185, 129, 0.25);
  box-shadow: 
    0 4px 8px rgba(0, 0, 0, 0.15),
    0 8px 16px rgba(0, 0, 0, 0.15),
    0 16px 32px rgba(0, 0, 0, 0.15),
    0 0 24px rgba(16, 185, 129, 0.1);
}

/* 避免懸浮時重繪 */
.management-card:hover * {
  transform: translateZ(0);
}

/* 卡片圖標 */
.card-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 8px;
}

.card-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

/* 卡片內容 */
.card-content {
  flex: 1;
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  letter-spacing: 0.5px;
}

.card-description {
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.5;
  margin: 0;
}

/* 動作按鈕 - 遵循按鈕規範 */
.card-action-btn {
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-gold-dark) 100%);
  color: var(--text-inverse);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all var(--transition-fast) var(--ease-out);
  position: relative;
  overflow: hidden;
  
  /* 按鈕陰影 */
  box-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

/* 按鈕光澤動畫 - 遵循規範 */
.card-action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s var(--ease-out);
}

/* 按鈕懸浮效果 - 遵循規範 */
.card-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 4px 8px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 0 16px rgba(16, 185, 129, 0.3);
}

.card-action-btn:hover::before {
  left: 100%;
}

/* 按鈕按下效果 - 遵循規範 */
.card-action-btn:active {
  transform: translateY(0);
  box-shadow: 
    0 1px 2px rgba(0, 0, 0, 0.2),
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* 核心動畫 - 遵循規範 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes shimmer {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 100% 0;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(16, 185, 129, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(16, 185, 129, 0.5);
  }
}

/* 響應式設計 - 遵循規範斷點 */
@media (max-width: 1280px) {
  .management-grid {
    max-width: 1024px;
  }
}

@media (max-width: 1024px) {
  .management-grid {
    max-width: 768px;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
}

/* 移動端滑動優化 */
@supports (-webkit-touch-callout: none) {
  .admin-dashboard-root {
    /* iOS 彈性滾動 */
    -webkit-overflow-scrolling: touch;
  }
}

/* 防止移動端滾動時的卡頓 */
@media (hover: none) and (pointer: coarse) {
  .management-card {
    /* 移動端關閉複雜動畫 */
    transition: transform 0.2s ease;
  }
  
  .management-card:active {
    transform: scale(0.98);
  }
}

@media (max-width: 768px) {
  .admin-dashboard-root {
    padding: 24px 16px 60px; /* 增加底部間距 */
    /* 移動端滾動優化 */
    overscroll-behavior: contain;
  }
  
  .page-title {
    font-size: 28px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .management-grid {
    gap: 16px;
  }
  
  .management-card {
    padding: 20px;
    /* 移動端觸控優化 */
    touch-action: manipulation;
  }
  
  /* 移動端滾動條更細 */
  .admin-dashboard-root::-webkit-scrollbar {
    width: 6px;
  }
}

@media (max-width: 640px) {
  .management-grid {
    grid-template-columns: 1fr;
  }
  
  .card-title {
    font-size: 18px;
  }
  
  .card-description {
    font-size: 13px;
  }
}

/* 無障礙支持 - 遵循規範 */
.management-card:focus-visible {
  outline: 2px solid var(--primary-gold);
  outline-offset: 4px;
}

.card-action-btn:focus-visible {
  outline: 2px solid var(--primary-gold-light);
  outline-offset: 2px;
}

/* 減少動畫效果 - 遵循規範 */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* 確保最小觸摸目標 - 遵循規範 44x44px */
.card-action-btn {
  min-height: 44px;
}

@media (pointer: coarse) {
  .management-card {
    min-height: 44px;
  }
}
</style> 